import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SearchInput from 'assets/components/inputs/search/SearchInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { buildOrSearchRegex } from 'assets/utils/data/Regex';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import { compact, isEmpty, set, uniq } from 'lodash';
import LossCode from 'models/claims/lossCode/Model.LossCode';
import LineItem from 'models/enums/LineItem';
import { useState } from 'react';

type Props = {
  data: Utils.FormData<Model.IClaimTypeJobItemTemplate>;
  lossCodes: LossCode[];
  onSave(data: Model.IClaimTypeJobItemTemplate): void;
  onCancel(): void;
};
export default function ClaimTypeJobItemTemplatesEditForm(props: Props) {
  const { lang, translate } = getLocales();
  const [{ limit, offset }, setLimits] = useState({ limit: defaultLimit, offset: defaultOffset });
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>();
  const [showSelectedPermissionsOnly, setShowSelectedPermissionsOnly] = useState(false);

  const filtered = props.lossCodes?.filter((it) => {
    const doesMatchSearch = !search ? true : buildOrSearchRegex(search).test(it.descnEn.toLowerCase());
    const doesMatchCategory = !category ? true : buildOrSearchRegex(category).test(it.category.toLowerCase());

    return doesMatchCategory && doesMatchSearch;
  });

  const categories = compact(uniq(props.lossCodes?.map((it) => it.category)));
  return (
    <>
      <ModalForm<Utils.FormData<Model.IClaimTypeJobItemTemplate>>
        subcomponents={ThemeModalFormDefault}
        dependencyArray={[props.data]}
        title={lang.jobItemTemplate}
        default={
          props.data || {
            jobItemTypeCode: undefined,
            lossCodes: [],
            correction: 0.0,
          }
        }
        onCancel={props.onCancel}
        onSubmit={props.onSave}
        validation={(data, errors) => {
          if (!data.jobItemTypeCode) set(errors, 'jobItemTypeCode', lang.required);
          if (!data.lossCodes || isEmpty(data.lossCodes)) set(errors, 'lossCodes._objectError', lang.required);
        }}
        render={({ form }) => {
          const lossCodeList = compact(
            showSelectedPermissionsOnly
              ? filtered?.filter((lossCode) => form.data.lossCodes?.find((it) => it.id === lossCode.id))
              : filtered
          ).slice(offset * limit, offset * limit + limit);

          const areAllSelected = lossCodeList?.every((d) => form.data.lossCodes?.some((l) => l.id === d.id));
          const areNoneSelected = !lossCodeList?.some((d) => form.data.lossCodes?.some((l) => l.id === d.id));
          return (
            <>
              <FormWrapper>
                <SelectInput
                  name="jobItemTypeCode"
                  label={lang.typeCode}
                  data={Object.values(LineItem)
                    .filter((it) => it !== 'Deductible')
                    .map((it) => ({ id: it, title: translate(it) }))}
                  value={form.data?.jobItemTypeCode}
                  onChange={form.update}
                  error={form.errors?.jobItemTypeCode}
                />
                &nbsp;
                <NumberInput
                  label={lang.requestedAmount}
                  name="correction"
                  value={form.data?.correction}
                  onChange={form.update}
                  style={{ input: { textAlign: 'right' } }}
                  iconBefore={<Icon class="fas-dollar-sign" />}
                />
              </FormWrapper>
              <h3 style={{ padding: `0 ${Theme.Size.L}`, display: 'flex' }}>
                {lang.lossCodes}
                <span style={{ marginLeft: 'auto' }}>
                  {form.data.lossCodes?.length ?? 0}/{props.lossCodes?.length ?? 0} {lang.itemsSelected}
                </span>
              </h3>

              <table className={ThemeTableSimple}>
                <thead>
                  <tr>
                    <th>
                      <Button
                        class={{ button: ThemeButtonCircle }}
                        media="fas-tasks"
                        onClick={() => setShowSelectedPermissionsOnly((it) => !it)}
                        active={showSelectedPermissionsOnly}
                        htmlElementProps={{ title: lang.showOnlySelected }}
                      />
                    </th>
                    <th colSpan={3}>
                      <SearchInput
                        value={search}
                        onSearch={(newSearch) => {
                          setSearch(newSearch);
                          setLimits((old) => ({ ...old, offset: 0 }));
                        }}
                        threshold={2}
                        placeholder={`${lang.search}...`}
                      />
                    </th>
                    <th colSpan={1}>
                      <SelectInput
                        name="categoryFilter"
                        data={categories.map((it) => ({ id: it, title: translate(it) }))}
                        value={category}
                        onChange={({ categoryFilter }) => setCategory(categoryFilter)}
                        placeholder={lang.category}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <Button
                        class={{ button: ThemeButtonCircle }}
                        media={
                          areAllSelected ? 'fas-check-square' : areNoneSelected ? 'far-square' : 'fas-minus-square'
                        }
                        disabled={!filtered?.length}
                        onClick={() => {
                          form.update({
                            lossCodes: areAllSelected ? [] : filtered,
                          });
                        }}
                        htmlElementProps={{ title: lang.selectAll }}
                      />
                    </th>
                    <th>{lang.code}</th>
                    <th>{lang.descnEn}</th>
                    <th>{lang.notes}</th>
                    <th>{lang.category}</th>
                  </tr>
                </thead>
                <tbody>
                  <DataMap
                    data={lossCodeList}
                    render={({ data: lossCode }) => {
                      const currentLossCode = form.data.lossCodes?.find((it) => it.id === lossCode.id);

                      return (
                        <tr key={lossCode.id}>
                          <td>
                            <Button
                              class={{ button: ThemeButtonCircle }}
                              htmlElementProps={{
                                title: currentLossCode ? lang.unselect : lang.select,
                              }}
                              onClick={() => {
                                if (currentLossCode)
                                  form.update({
                                    lossCodes: form.data.lossCodes?.filter((it) => it?.id !== lossCode.id),
                                  });
                                else form.update({ lossCodes: [...(form.data.lossCodes || []), lossCode] });
                              }}
                              media={currentLossCode ? 'fas-check-square' : 'far-square'}
                            />
                          </td>
                          <td>{lossCode.code}</td>
                          <td>{lossCode.descnEn}</td>
                          <td>{lossCode.notes}</td>
                          <td>{lossCode.category}</td>
                        </tr>
                      );
                    }}
                  />
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5}>
                      <Pagination
                        class={ThemePaginationRounded}
                        count={filtered?.length}
                        offset={offset}
                        limit={limit}
                        onLimitChange={(limit) => setLimits({ limit, offset: 0 })}
                        onOffsetChange={(offset) => setLimits((old) => ({ ...old, offset }))}
                      />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </>
          );
        }}
      />
    </>
  );
}
